import type { PermissionEnum } from '@Application/Security/Roles';
import type { PermissionGateMode } from '@UI/Providers/PermissionProvider/useHasPermission';
import useUserPermissions from '@UI/Providers/PermissionProvider/useUserPermissions';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Message, Spinner } from '@verclam/ui-kit';

const spinnerContainerStyle = css`
	display: flex;
	justify-content: center;
	padding: ${theme.layout.gutter};
`;

const messageContainerStyle = css`
	padding: ${theme.layout.gutter};
`;

type Props = {
	readonly permission: PermissionEnum | readonly PermissionEnum[];
	readonly mode?: PermissionGateMode;
	/** French description of the action being attempted (e.g. "gérer les agences"), shown in the
	 * refusal message alongside the required permission. */
	readonly action: string;
	readonly children: React.ReactNode;
};

/**
 * Route guard: waits for `isResolved` before deciding, since `hasPermission` is indistinguishable
 * from "denied" before the permissions fetch resolves. Redirecting on that unresolved state would
 * bounce every authorized user to "access denied" on each page load.
 */
const RequirePermission: React.FC<Props> = ({ permission, mode = 'all', action, children }) => {
	const { isResolved, hasPermission, hasAnyPermission, hasAllPermissions } = useUserPermissions();

	if (!isResolved) {
		return (
			<div css={spinnerContainerStyle}>
				<Spinner aria-label="Vérification des droits d'accès" />
			</div>
		);
	}

	// `typeof` narrows a `readonly PermissionEnum[]` correctly, unlike `Array.isArray`, which only
	// excludes a mutable `any[]` from the negated branch.
	const granted =
		typeof permission === 'string'
			? hasPermission(permission)
			: mode === 'any'
				? hasAnyPermission(permission)
				: hasAllPermissions(permission);

	if (!granted) {
		const requiredPermissions = typeof permission === 'string' ? permission : permission.join(' ou ');

		return (
			<div css={messageContainerStyle}>
				<Message
					severity="error"
					text={`Vous n'avez pas le droit nécessaire pour ${action} (droit requis : ${requiredPermissions}).`}
				/>
			</div>
		);
	}

	return children;
};

export default RequirePermission;
